import { useInstance } from '../support/ioc/useInstance';
import { useSubscription } from '@autopylot-internal/state';
import { FileUploader } from '../notes/FileUploader.ts';
import { AircraftNotesModelFactory } from './AircraftNotesModelFactory.ts';
import { AircraftLogEntryForm } from './AircraftLogEntryForm.tsx';
import { Page } from '../components/page/Page.tsx';
import { WrapWithCardOnLargerScreen } from '../components/card/WrapWithCardOnLargerScreen.tsx';
import { AlertPresenter } from '../app/AlertBinder.tsx';
import { HistoryViewModel } from '../app/HistoryViewModel.ts';
import { useSubscriptionAsync } from '../support/hooks/useSubscriptionAsync.tsx';
import { IAttachment } from '../notes/NotesClient.ts';
import { Button } from '../components/button/Button.tsx';
import { ITrackingEvent, TrackingService } from '../support/tracking/TrackingService.ts';
import { IAircraftNote, IAircraftNoteResponse } from './IAircraftNote.ts';
import { initCap } from '../support/helpers/format.ts';

export const AircraftLogEntryEditPage: React.FC<{aircraftId: string, noteId: string}> = ({ aircraftId, noteId }) => {
    const alert = useInstance(AlertPresenter);
    const history = useInstance(HistoryViewModel);
    const tracker = useInstance(TrackingService);

    const uploader = useInstance(FileUploader);
    useSubscription(() => uploader);

    // NOTE: model creation must be inside subscription, otherwise model is recreated on every emit
    const notesFactory = useInstance(AircraftNotesModelFactory);
    const noteVM = useSubscriptionAsync(async () => await notesFactory.forAircraft(aircraftId).createEditNoteVM(noteId));

    const track = (eventName: ITrackingEvent) => {
        if (noteVM) {
            tracker.track(eventName, () => {
                const note = noteVM.getNote() as IAircraftNote;
                return {
                    title: note.title,
                    dateTime: note.otherAttributes.dateTime,
                    type: initCap(note.otherAttributes.type),
                    html: note.html,
                    attachments: note.attachments
                };
            });
        }
    };

    const goBack = () => {
        history.back(`/fleet/${aircraftId}/log`);
    };

    const onCancel = () => {
        track('Drone Log Entry Viewed');
        goBack();
    };

    const saveAndClose = async () => {
        if (!noteVM){
            return;
        }
        try {
            await noteVM.save();
            track('Drone Log Entry Edited');
            goBack();
        }
        catch (err: any){
            alert.showAndLogError(err);
        }
    };

    const onAttachmentAdded = (attachment: IAttachment) => {
        tracker.track('Log Entry Attachment Uploaded', () => ({
            type: attachment.url.substring(attachment.url.lastIndexOf('.')) || ''
        }));
    };

    const onAttachmentRemoved = (attachment: IAttachment) => {
        tracker.track('Log Entry Attachment Removed', () => ({
            type: attachment.url.substring(attachment.url.lastIndexOf('.')) || ''
        }));
    };

    const deleteAndClose = async () => {
        if (!noteVM){
            return;
        }

        const destroyOrCancel = await alert.confirmDestructiveAction({
            header: 'Delete Log Entry?',
            message: 'This log entry will be deleted and cannot be recovered.'
        });

        if (destroyOrCancel === 'destroy'){
            try {
                await noteVM.delete();

                tracker.track('Drone Log Entry Deleted');

                goBack();
            }
            catch (err: any){
                alert.showAndLogError(err);
            }
        }
    };

    return (

        <Page title="Edit Entry" onBack={onCancel} backLabel="Cancel" onAction={saveAndClose} actionLabel="Save">
            <WrapWithCardOnLargerScreen>
                {noteVM &&
                    <>
                        <AircraftLogEntryForm
                            note={noteVM.getNote()}
                            noteChanged={updatedNote => noteVM.setNote(updatedNote as IAircraftNoteResponse)}
                            attachmentAdded={onAttachmentAdded}
                            attachmentRemoved={onAttachmentRemoved}
                        />

                        <div style={{margin: 20, marginTop: 35}}>
                            <Button expand="block" color="danger" dataType="delete" fill='outline' onClick={deleteAndClose}>Delete Entry</Button>
                        </div>
                    </>
                }
            </WrapWithCardOnLargerScreen>
        </Page>
    );
};
